'use client';

import Form from 'next/form';
import {
  MouseEventHandler,
  useActionState,
  useCallback,
  useEffect,
} from 'react';
import { useFormStatus } from 'react-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import RvIcon from '~/assets/vector/rv.svg';
import CallToAction from '~/components/atoms/CallToAction';
import Heading from '~/components/atoms/Heading';
import Price from '~/components/atoms/Price';
import Dialog from '~/components/molecules/Dialog';
import FormInput from '~/components/molecules/FormInput';
import {
  sendPropertyContactMessage,
  type SendPropertyContactMessageState,
} from '~/lib/actions/property-contact';
import { modelProperty } from '~/lib/api/models';
import routeGenerator from '~/lib/api/routes';
import { useDialogContext } from '~/lib/hooks/useDialogContext';
import {
  getMainPropertyCategory,
  getPropertyPrice,
  propertyIsRent,
} from '~/lib/utils/properties';

const ContactFormContents: React.FC<{
  property: z.infer<typeof modelProperty.schema>['property'];
  state: SendPropertyContactMessageState;
}> = ({ property, state }) => {
  const status = useFormStatus();
  const price = getPropertyPrice(property);
  const reference = property.propertyMeta.displayReference;

  const { close } = useDialogContext();

  useEffect(() => {
    switch (state.status) {
      case 'success': {
        toast.success('Message envoyé avec succès', {
          description: 'Nous reviendrons vers vous dans les plus brefs délais',
          duration: 8000,
          dismissible: true,
        });

        close();

        break;
      }

      case 'error': {
        toast.error('Une erreur est survenue', {
          description: 'Veuillez réessayer plus tard',
          duration: 8000,
          dismissible: true,
        });

        break;
      }
    }
  }, [close, state.status]);

  const selectTextContent = useCallback<MouseEventHandler<HTMLElement>>(
    (event) => {
      const range = document.createRange();

      range.selectNodeContents(event.currentTarget);

      const selection = window.getSelection();

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
    []
  );

  return (
    <div className="text-brand-primary flex flex-col items-center gap-y-4">
      <RvIcon className="text-brand-quinary h-3" />

      <Heading as="h2" level={3}>
        Nous contacter
      </Heading>

      <hr className="bg-brand-quinary/50 h-px w-20 border-none" />

      <div className="relative z-10 flex flex-col gap-y-1 text-center">
        <Heading as="h3" level={4}>
          {property.title}
        </Heading>

        {(price || reference) && (
          <div className="flex items-center justify-center gap-x-2">
            {price && (
              <Price
                value={price}
                supText={propertyIsRent(property) ? 'CC' : null}
                className="text-brand-quinary relative text-sm"
              />
            )}

            {price && reference && (
              <hr className="bg-brand-quinary/50 h-4 w-px border-0" />
            )}

            {reference && (
              <div className="text-brand-quinary relative flex text-sm">
                <span>Reference : </span>
                <data value={reference} onClick={selectTextContent}>
                  {reference}
                </data>
              </div>
            )}
          </div>
        )}
      </div>

      <fieldset className="hidden">
        <input
          type="hidden"
          name="meta.property.id"
          value={property.propertyMeta.displayReference || ''}
        />

        <input type="hidden" name="meta.property.slug" value={property.slug} />
        <input
          type="hidden"
          name="meta.property.title"
          value={property.title}
        />

        <input
          type="hidden"
          name="meta.property.permalink"
          value={routeGenerator.property({
            category: getMainPropertyCategory(property) || 'vente',
            slug: property.slug,
          })}
        />

        <input
          type="hidden"
          name="meta.contact.name"
          value={property.propertyMeta.contactName || ''}
        />

        <input
          type="hidden"
          name="meta.contact.email"
          value={property.propertyMeta.contactEmail || ''}
        />
      </fieldset>

      <div className="grid w-full grid-cols-2 gap-x-6 gap-y-4">
        <FormInput
          type="text"
          label="Prénom"
          name="first_name"
          autoComplete="given-name"
          className="col-span-2 lg:col-span-1"
          placeholder="Prénom"
          disabled={status.pending}
          required
        />

        <FormInput
          type="text"
          label="Nom"
          name="last_name"
          autoComplete="family-name"
          className="col-span-2 lg:col-span-1"
          placeholder="Nom"
          disabled={status.pending}
          required
        />

        <FormInput
          type="email"
          label="Email"
          name="email"
          autoComplete="email"
          className="col-span-2 lg:col-span-1"
          placeholder="adresse@email.fr"
          disabled={status.pending}
          required
        />

        <FormInput
          type="tel"
          label="Téléphone"
          name="phone"
          autoComplete="home tel"
          className="col-span-2 lg:col-span-1"
          placeholder="01 23 45 67 89"
          disabled={status.pending}
        />

        <FormInput
          type="textarea"
          label="Message"
          name="message"
          className="col-span-2"
          placeholder="Votre message"
          disabled={status.pending}
        />
      </div>

      <CallToAction
        as="button"
        type="submit"
        variant="dark"
        disabled={status.pending}
        loading={status.pending}
      >
        Envoyer le message
      </CallToAction>
    </div>
  );
};

const initialState: SendPropertyContactMessageState = {
  status: 'idle',
};

const ContactForm: React.FC<{
  property: z.infer<typeof modelProperty.schema>['property'];
}> = ({ property }) => {
  const [state, formAction, pending] = useActionState(
    sendPropertyContactMessage,
    initialState
  );

  return (
    <Form action={formAction} disabled={pending}>
      <ContactFormContents property={property} state={state} />
    </Form>
  );
};

const ContactDialog: React.FC<{
  title: string;
  property: z.infer<typeof modelProperty.schema>['property'];
}> = ({ title, property }) => (
  <Dialog
    trigger={(openDialog) => (
      <CallToAction as="button" type="button" level={2} onClick={openDialog}>
        {title}
      </CallToAction>
    )}
    className="w-200 max-w-[calc(100svw-32px)] md:max-w-[calc(100svw-80px)]"
  >
    <ContactForm property={property} />
  </Dialog>
);

export default ContactDialog;
